import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CaseIdSchema, HighlightSchema, type Highlight } from '@casetrace/shared';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

export const highlightsQueryKey = (caseId: string) => ['highlights', caseId] as const;

function toHighlightMap(docs: Array<{ id: string; data: () => unknown }>): Map<string, Highlight> {
  const map = new Map<string, Highlight>();
  for (const doc of docs) {
    const parsed = HighlightSchema.safeParse({
      ...(doc.data() as Record<string, unknown>),
      eventId: doc.id,
    });
    if (parsed.success) {
      map.set(parsed.data.eventId, parsed.data);
    }
  }
  return map;
}

async function fetchHighlights(caseId: string): Promise<Map<string, Highlight>> {
  const parsedCaseId = CaseIdSchema.parse(caseId);
  const ref = collection(db, 'cases', parsedCaseId, 'highlights');
  const snap = await getDocs(ref);
  return toHighlightMap(snap.docs);
}

/**
 * Realtime-abonnement på stjernemerkinger for en sak.
 * Data lagres som Map eventId → Highlight for O(1)-oppslag i tidslinjen.
 */
export function useCaseHighlights(caseId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: highlightsQueryKey(caseId),
    queryFn: () => fetchHighlights(caseId),
    enabled: Boolean(caseId),
  });

  useEffect(() => {
    if (!caseId) return;
    const parsedCaseId = CaseIdSchema.parse(caseId);
    const ref = collection(db, 'cases', parsedCaseId, 'highlights');
    return onSnapshot(ref, (snap) => {
      queryClient.setQueryData(highlightsQueryKey(caseId), toHighlightMap(snap.docs));
    });
  }, [caseId, queryClient]);

  return query;
}
